import { lazy, Suspense, useContext, useEffect, useMemo, useState } from 'react';
import { SettingsCache } from '~/lib/consts/settings-cache';
import { GetAccountId } from '../../../lib/helpers/AuthHelper';
import { useAuth } from 'react-oidc-context';
import useExternalSearch from '../client/useExternalSearch';
import { useSmartSendApi } from '~/lib/api/smartsend/use-smartsend-api';
import { ExternalSearchBooked } from '~/apps/smartsend/lib/consts/ListyConsts';
import { SmartSendContext } from '~/apps/smartsend/lib/context/SmartSendContext';
import { useTenant } from '@parcel2go/npm-p2g-app';
import { useTranslation } from 'react-i18next';

const ProductFruits = lazy(() => import('react-product-fruits'));

interface props {
  tab: string;
  isXs: boolean;
}

const WalkthroughPrompt: React.FC<props> = (props) => {
  const { i18n } = useTranslation();
  const { state } = useContext(SmartSendContext);
  const auth = useAuth();
  const { settings, tenant } = useTenant();
  const { getExternalSearchQueryCountByPaidSource, getExternalSearchQueryMarketplaceOrderCount } = useExternalSearch();
  const { getMarketplaceIntegrations } = useSmartSendApi();
  const productFruitsEnabled = SettingsCache.settings.Features.includes('ProductFruits');
  const [shouldRender, setShouldRender] = useState(false);
  const [meetsOrderLimitRequirement, setMeetsOrderLimitRequirement] = useState<boolean>(false);
  const [additionalProps, setAdditionalProps] = useState<{ [key: string]: string | number } | undefined>(undefined);
  const workspace = useMemo(() => settings['UI-SERVERLESS.PUBLIC.PRODUCTFRUITS.MYACCOUNT.WORKSPACEID'], [settings]);
  const lang = i18n.resolvedLanguage || 'en';

  useEffect(() => {
    const timer = setTimeout(() => {
      setShouldRender(true);
    }, 1500);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!shouldRender) {
      return;
    }

    try {
      const getData = async () => {
        // Dont show if they haven't met the order limit requirement
        const orderCount = await getExternalSearchQueryMarketplaceOrderCount(ExternalSearchBooked);

        // If they haven't met the order limit requirement, check if they have shopify or ebay accounts connected and if they have recent orders from those platforms to show more relevant messaging
        if (!SettingsCache.settings.Features.includes('ProductFruitsFirstOrderOnly') || orderCount === 0) {
          setMeetsOrderLimitRequirement(true);
          const accounts = await getMarketplaceIntegrations();
          const shopifyOrderCount = await getExternalSearchQueryCountByPaidSource(['shopify'], ExternalSearchBooked);
          const ebayOrderCount = await getExternalSearchQueryCountByPaidSource(['ebay'], ExternalSearchBooked);
          setAdditionalProps({
            hasShopifyAccount: accounts?.result?.Providers?.some((x) => x.Id === 'shopify') ? 'true' : 'false',
            recentShopifyOrders: shopifyOrderCount ?? 0,
            hasEbayAccount: accounts?.result?.Providers?.some((x) => x.Id === 'ebay') ? 'true' : 'false',
            recentEbayOrders: ebayOrderCount ?? 0
          });
        }
      };
      getData();
    } catch (e) {
      setAdditionalProps({});
    }
  }, [
    shouldRender,
    getMarketplaceIntegrations,
    getExternalSearchQueryCountByPaidSource,
    getExternalSearchQueryMarketplaceOrderCount
  ]);

  const userInfo = useMemo(() => {
    if (
      !workspace ||
      !meetsOrderLimitRequirement ||
      props.isXs ||
      !auth?.user?.profile ||
      !productFruitsEnabled ||
      !shouldRender ||
      additionalProps === undefined ||
      !state?.customerInitialised
    ) {
      return null;
    }

    return {
      username: GetAccountId(auth?.user?.profile),
      firstname: auth?.user?.profile.given_name || '',
      lastname: auth?.user?.profile.family_name || '',
      props: {
        ...additionalProps,
        tenant: (tenant?.split('.').slice(0, 2).join('.') || '').toUpperCase()
      }
    };
  }, [
    auth?.user?.profile,
    additionalProps,
    props.isXs,
    productFruitsEnabled,
    shouldRender,
    state?.customerInitialised,
    workspace,
    meetsOrderLimitRequirement,
    tenant
  ]);

  if (!userInfo) {
    return null;
  }

  return (
    <Suspense fallback={<></>} key={lang}>
      <ProductFruits projectCode={workspace} language={lang} {...userInfo} />
    </Suspense>
  );
};

export default WalkthroughPrompt;
